// Budget Pacing Alert Component (S015)
import React from 'react';

interface Props {
  campaignId: string;
  campaignName: string;
  budgetTotal: number;
  budgetSpent: number;
  daysTotal: number;
  daysElapsed: number;
  pacingStatus: 'on_track' | 'underspending' | 'overspending';
}

export const BudgetPacingAlert: React.FC<Props> = ({
  campaignName,
  budgetTotal,
  budgetSpent,
  daysTotal,
  daysElapsed,
  pacingStatus,
}) => {
  const budgetPercentage = (budgetSpent / budgetTotal) * 100;
  const timePercentage = (daysElapsed / daysTotal) * 100;
  const pacingDifference = budgetPercentage - timePercentage;

  const getStatusColor = () => {
    switch (pacingStatus) {
      case 'on_track': return 'bg-success';
      case 'underspending': return 'bg-warning';
      case 'overspending': return 'bg-danger';
    }
  };

  const getStatusText = () => {
    switch (pacingStatus) {
      case 'on_track': return 'On Track';
      case 'underspending': return 'Underspending';
      case 'overspending': return 'Overspending';
    }
  };

  return (
    <div className="bg-white dark:bg-boxdark rounded-lg p-4 border border-stroke dark:border-strokedark">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-black dark:text-white">{campaignName}</h4>
          <span className={`inline-block px-2 py-1 rounded text-xs font-medium text-white mt-2 ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        <div className="text-right">
          <p className="text-sm text-bodydark dark:text-bodydark1">
            ${budgetSpent.toLocaleString()} / ${budgetTotal.toLocaleString()}
          </p>
          <p className="text-xs text-bodydark dark:text-bodydark1 mt-1">
            Day {daysElapsed} of {daysTotal}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span>Budget</span>
            <span>{budgetPercentage.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-stroke dark:bg-strokedark rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${getStatusColor()}`}
              style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span>Time</span>
            <span>{timePercentage.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-stroke dark:bg-strokedark rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${Math.min(timePercentage, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {Math.abs(pacingDifference) > 5 && (
        <div className="mt-3 text-sm text-bodydark dark:text-bodydark1">
          {pacingDifference > 0 ? (
            <p>⚠️ Spending {pacingDifference.toFixed(1)}% faster than expected</p>
          ) : (
            <p>📊 Spending {Math.abs(pacingDifference).toFixed(1)}% slower than expected</p>
          )}
        </div>
      )}
    </div>
  );
};
